import { useCallback, useMemo, useState } from 'react'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon, MinusSignIcon } from '@hugeicons/core-free-icons'

import {
  formatScore,
  replaceManagerPlaceholders,
  replaceOrganizationName,
} from '../utils/assessmentQuestionsTable.utils'
import { CategoryContent } from './CategoryContent'
import type { Assessment } from '../types'
import {
  PretextedAccordion,
  PretextedAccordionContent,
  PretextedAccordionItem,
} from '@/components/common/pretexted-accordion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface PositionQuestion {
  id: string
  name: string
  subName: string
  managerText: string
  directReportText: string
}

export interface AssessmentQuestionsTableProps {
  assessment: Assessment | undefined
  positionQuestions: Array<PositionQuestion>
  currentUserId: string
  organizationName: string
  directReportName: string
  isLoading: boolean
  error: Error | null
}

export function AssessmentQuestionsTable({
  assessment,
  positionQuestions,
  currentUserId,
  organizationName,
  directReportName,
  isLoading,
  error,
}: AssessmentQuestionsTableProps) {
  // Track open categories to toggle +/- icon
  const [openCategories, setOpenCategories] = useState<Array<string>>([])

  // Track which accordions have been opened at least once (to enable lazy loading)
  const [openedCategories, setOpenedCategories] = useState<Set<string>>(
    new Set(),
  )

  // Determine user role
  const viewAsManager = currentUserId === assessment?.managerId

  // Bind placeholder replacers with resolved data
  const boundReplaceOrganizationName = useCallback(
    (text: string) => replaceOrganizationName(text, organizationName),
    [organizationName],
  )

  const boundReplaceManagerPlaceholders = useCallback(
    (text: string) =>
      replaceManagerPlaceholders(text, directReportName, organizationName),
    [directReportName, organizationName],
  )

  // Group questions by category (name field)
  const groupedQuestions = useMemo(() => {
    const groups = new Map<string, Array<PositionQuestion>>()

    positionQuestions.forEach((question) => {
      const category = question.name
      if (!groups.has(category)) {
        groups.set(category, [])
      }
      groups.get(category)!.push(question)
    })

    return Array.from(groups.entries()).map(([category, questions]) => ({
      category,
      questions,
    }))
  }, [positionQuestions])

  // Handle accordion value change — track open state (for icon) + lazy fetching
  const handleAccordionChange = (value: Array<string>) => {
    setOpenCategories(value)
    setOpenedCategories((prev) => {
      const newSet = new Set(prev)
      value.forEach((cat) => newSet.add(cat))
      return newSet
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <Card
            key={i}
            className="p-6 bg-muted/30 rounded-lg border-none shadow-none overflow-hidden"
          >
            <div className="flex items-center justify-between py-0">
              <Skeleton className="h-7 w-36" />
              <div className="flex items-center justify-center w-10 h-10 rounded-lg border-2 border-muted/80 bg-muted/80">
                <Skeleton className="h-5 w-5" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">
            Error Loading Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error.message || 'Failed to load assessment details'}
          </p>
        </CardContent>
      </Card>
    )
  }

  // No data state
  if (!assessment || groupedQuestions.length === 0) {
    return (
      <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
        <CardHeader className="p-0 mb-4">
          <CardTitle>No Assessment Data</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <p className="text-sm text-muted-foreground">
            No assessment questions found.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <PretextedAccordion
      type="multiple"
      className="space-y-4"
      onValueChange={handleAccordionChange}
    >
      {groupedQuestions.map(({ category, questions }) => (
        <Card
          key={category}
          className="p-6 bg-muted/30 rounded-lg border-none shadow-none overflow-hidden"
        >
          <PretextedAccordionItem value={category} className="border-none">
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex w-full items-center justify-between py-0 hover:no-underline focus:outline-none">
                <span className="font-semibold text-lg">{category}</span>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg border-2 border-muted/80 bg-muted/80 hover:bg-muted transition-colors shrink-0">
                  {openCategories.includes(category) ? (
                    <HugeiconsIcon
                      icon={MinusSignIcon}
                      className="text-muted-foreground size-5 shrink-0"
                    />
                  ) : (
                    <HugeiconsIcon
                      icon={Add01Icon}
                      className="text-muted-foreground size-5 shrink-0"
                    />
                  )}
                </div>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <PretextedAccordionContent innerClassName="pb-0">
              <CategoryContent
                category={category}
                questions={questions}
                assessment={assessment}
                viewAsManager={viewAsManager}
                replaceManagerPlaceholders={boundReplaceManagerPlaceholders}
                replaceOrganizationName={boundReplaceOrganizationName}
                formatScore={formatScore}
                shouldFetch={openedCategories.has(category)}
              />
            </PretextedAccordionContent>
          </PretextedAccordionItem>
        </Card>
      ))}
    </PretextedAccordion>
  )
}
